import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "w-full mb-4"
}
const _hoisted_2 = { class: "relative flex items-center w-full h-[22px] max-h-[22px] bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "flex flex-wrap items-start w-full justify-center" }
const _hoisted_5 = ["onClick", "onMouseover"]
const _hoisted_6 = ["src", "srcset"]
const _hoisted_7 = { class: "text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1" }
const _hoisted_8 = { class: "flex flex-wrap items-start w-full justify-center" }
const _hoisted_9 = ["onClick", "onMouseover"]
const _hoisted_10 = ["src", "srcset"]
const _hoisted_11 = { class: "text-parameter-text text-[10px] overflow-hidden max-w-13 mx-auto text-center mt-1" }

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import ItemV3MostSelected from "@/components/itemV3/parameter/itemV3MostSelected.vue";
import {computed, inject, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import {getCurrentText} from "@/assets/ts/itemText";

const imageWidthPercent: number = 30
const boxIdx = "finish"

const variantIdx = "discs.*.finish"


export default /*@__PURE__*/_defineComponent({
  __name: 'itemV3DiscsFinish',
  props: ["item"],
  emits: ["patch"],
  setup(__props, { emit: __emit }) {


const {t} = useI18n();

const emit = __emit;
const props = __props;

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const loading = ref(true)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

const currentDiscsFinishText = computed(() => {
  return getCurrentText(configureAll.value ? -1 : itemIdx.value, variantIdx)
})

const open = ref(false)

const discVariants = computed(() => open.value ? parametersVariations.getVariants() : [])

/**
 * color variants
 */

const activeDiscIdx = ref(0)

const discSliderStyle = computed(() => {
  const count = discVariants.value.length;
  if (count === 0 || activeDiscIdx.value < 0 || activeDiscIdx.value >= count) return {display: 'none'};

  const width = 100 / count;
  const left = activeDiscIdx.value * width;

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  };
});

const finishVariantsOfDisc = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return []
  return discVariants.value[activeDiscIdx.value].values
})

const activeDisc = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return []
  return discVariants.value[activeDiscIdx.value]
})

const matchedDiscIdx = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return -1
  return discVariants.value[activeDiscIdx.value].values.findIndex((v: any) => v.match)
})

/**
 * image
 */

const hoverFinishIdx = ref(-1)

const setHoverFinishIdx = () => {
  if (matchedDiscIdx.value !== -1 && finishVariantsOfDisc.value.length >= matchedDiscIdx.value) {
    hoverFinishIdx.value = matchedDiscIdx.value
  }
}

watch(activeDisc, () => {
  setHoverFinishIdx()
})

watch(props.item, () => {
  setHoverFinishIdx()
})

const imageUrl = computed(() => parametersVariations.imageUrl(finishVariantsOfDisc.value, hoverFinishIdx.value))

const getFinishImageUrl = (finish: string, color: string, sizeX: number, sizeY: number) => {
  return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/finishes/' + sizeX + 'x' + sizeY + '/' + finish.toLowerCase() + '_' + color.toLowerCase() + '.webp'
}

const checkImageLoaded = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(true);
    img.src = url;
  });
};

const checkAllLoaded = async () => {
  const discs = discVariants.value;
  if (!discs.length) return;

  const allListsCreated = discs.every((disc: any) => disc.values && disc.values.length > 0);
  if (!allListsCreated) return;

  const imageUrls = discs.flatMap((disc: any) => disc.values.map((color: any) => getFinishImageUrl(color.idx, color.additional_value, 400, 300)));
  await Promise.all(imageUrls.map((url: string) => checkImageLoaded(url)));

  loading.value = false;
};

watch(discVariants, () => {
  checkAllLoaded();
  if (discVariants.value.length > 0) {
    loading.value = true;
  }
}, {immediate: true, deep: true});

/*
 * focus values
 */

const focusValueIdx = [
  "matt",
  "polished",
  "ice_matt",
  "hammered_matt",
  "sand_matt"
]

const getFocusValueIdx = computed(
    () => {
      if (matchedDiscIdx.value) {
        if (!focusValueIdx.includes(discVariants.value[activeDiscIdx.value].values[matchedDiscIdx.value].idx)) {
          return [
            discVariants.value[activeDiscIdx.value].values[matchedDiscIdx.value].idx,
            ...focusValueIdx.slice(0, -1)
          ]
        }
      }

      return focusValueIdx

    }
)

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * tip texts
 */

const getFinishTipTitle = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverFinishIdx.value]) {
    const key = activeDisc.value.values[hoverFinishIdx.value].idx.toLowerCase()
    return t('components.parameters.tips.finishes.' + key + '.title')
  } else {
    return ''
  }
})

const getFinishTipText = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverFinishIdx.value]) {
    const key = activeDisc.value.values[hoverFinishIdx.value].idx.toLowerCase()
    return t('components.parameters.tips.finishes.' + key + '.text')
  } else {
    return ''
  }
})

const getFinishTipImageUrl = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverFinishIdx.value]) {
    return getFinishImageUrl(activeDisc.value.values[hoverFinishIdx.value].idx, activeDisc.value.values[hoverFinishIdx.value].additional_value, 800, 600)
  } else {
    return ''
  }
})

onMounted(
    () => {
      setHoverFinishIdx()
    }
)


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ItemV3ParameterBox, {
    open: open.value,
    "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => ((open).value = $event)),
    "image-width-percent": imageWidthPercent,
    image: imageUrl.value,
    "header-image": getHeaderImage(),
    "header-image-srcset": getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w',
    title: "Oberfläche",
    subtitle: currentDiscsFinishText.value || 'Wählen Sie die Oberfläche für Ihre Trauringe',
    loading: loading.value,
    "tip-title": getFinishTipTitle.value,
    "tip-text": getFinishTipText.value,
    "tip-image": getFinishTipImageUrl.value
  }, {
    main: _withCtx(() => [
      (discVariants.value.length > 1)
        ? (_openBlock(), _createElementBlock("nav", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", {
                class: "absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none",
                style: _normalizeStyle(discSliderStyle.value)
              }, null, 4),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(discVariants.value, (disc, discIdx) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: discIdx,
                  type: "button",
                  class: _normalizeClass(["text-[10px] tracking-wided relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full", activeDiscIdx.value === discIdx ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700']),
                  onClick: ($event: any) => (activeDiscIdx.value = discIdx)
                }, " Scheibe " + _toDisplayString(discIdx + 1), 11, _hoisted_3))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true),
      _createVNode(ItemV3MostSelected),
      _createElementVNode("ul", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(finishVariantsOfDisc.value, (finish, finishIdx) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (getFocusValueIdx.value.includes(finish.idx.toString()))
              ? (_openBlock(), _createElementBlock("li", {
                  key: finishIdx,
                  class: _normalizeClass(["cursor-pointer flex flex-col py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] items-center justify-start", {
              'border-2 border-brand-main bg-[#f3f0e9]': finish.match,
              'border-2 border-transparent': !finish.match
            }]),
                  onClick: ($event: any) => (handleValuePatch(activeDisc.value.idx, finish)),
                  onMouseover: ($event: any) => (hoverFinishIdx.value = finishIdx as number),
                  onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (hoverFinishIdx.value = matchedDiscIdx.value))
                }, [
                  _createElementVNode("img", {
                    src: getFinishImageUrl(finish.idx, finish.additional_value, 400, 300),
                    srcset: getFinishImageUrl(finish.idx, finish.additional_value,800, 600),
                    class: "w-12 md:w-13 aspect-4:3 rounded-2xl"
                  }, null, 8, _hoisted_6),
                  _createElementVNode("span", _hoisted_7, _toDisplayString(finish.title), 1)
                ], 42, _hoisted_5))
              : _createCommentVNode("", true)
          ], 64))
        }), 256))
      ])
    ]),
    more: _withCtx(() => [
      _createElementVNode("ul", _hoisted_8, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(finishVariantsOfDisc.value, (finish, finishIdx) => {
          return (_openBlock(), _createElementBlock(_Fragment, null, [
            (!getFocusValueIdx.value.includes(finish.idx.toString()))
              ? (_openBlock(), _createElementBlock("li", {
                  key: finishIdx,
                  class: _normalizeClass(["cursor-pointer flex flex-col py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] items-center justify-start", {
              'border-2 border-brand-main bg-[#f3f0e9]': finish.match,
              'border-2 border-transparent': !finish.match
            }]),
                  onClick: ($event: any) => (handleValuePatch(activeDisc.value.idx, finish)),
                  onMouseover: ($event: any) => (hoverFinishIdx.value = finishIdx as number),
                  onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (hoverFinishIdx.value = matchedDiscIdx.value))
                }, [
                  _createElementVNode("img", {
                    src: getFinishImageUrl(finish.idx, finish.additional_value, 400, 300),
                    srcset: getFinishImageUrl(finish.idx, finish.additional_value,800, 600),
                    class: "w-12 md:w-13 aspect-4:3 rounded-2xl"
                  }, null, 8, _hoisted_10),
                  _createElementVNode("span", _hoisted_11, _toDisplayString(finish.title), 1)
                ], 42, _hoisted_9))
              : _createCommentVNode("", true)
          ], 64))
        }), 256))
      ])
    ]),
    _: 1
  }, 8, ["open", "image", "header-image", "header-image-srcset", "subtitle", "loading", "tip-title", "tip-text", "tip-image"]))
}
}

})